import { z } from 'zod';

import { entityIdSchema, nonEmptyTextSchema } from './primitives';

/**
 * Stable, server-owned description of a site. `siteId` is the public domain/media ID;
 * `authoritySiteId` is the existing trusted membership/document path segment. They may differ,
 * and callers must not infer one from the other.
 */
export const siteKindSchema = z.enum(['factory', 'marine', 'construction-site']);
export const siteLifecycleSchema = z.enum(['draft', 'active', 'retired']);

export const siteRecordSchema = z
  .object({
    schemaVersion: z.literal(1),
    tenantId: entityIdSchema,
    siteId: entityIdSchema,
    authoritySiteId: entityIdSchema,
    kind: siteKindSchema,
    lifecycle: siteLifecycleSchema,
    displayName: nonEmptyTextSchema.max(200),
    timezone: nonEmptyTextSchema.max(100),
    reportingDivisionId: entityIdSchema.nullable(),
  })
  .strict()
  .superRefine((record, context) => {
    if (record.siteId === record.authoritySiteId) return;
    // Different IDs are intentional only at the trusted boundary. The contract retains both so
    // each side can be validated rather than reconstructing an alias from a display value.
    if (!record.siteId.startsWith('site-')) {
      context.addIssue({
        code: 'custom',
        path: ['siteId'],
        message: 'Public site IDs that differ from authority IDs require the site- prefix',
      });
    }
  });

export type SiteKind = z.infer<typeof siteKindSchema>;
export type SiteLifecycle = z.infer<typeof siteLifecycleSchema>;
export type SiteRecord = z.infer<typeof siteRecordSchema>;
